/**
 * Assets & Trackers Overview Widget — Premium
 * Single widget: Total Assets, Active Trackers, Tracker assignment (assigned/unassigned + chart).
 * UI/UX: Visual hierarchy, consistent spacing scale, depth, typography (tabular numbers), micro-interactions.
 */

import { useMemo, memo } from "react";
import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import InventoryIcon from "@mui/icons-material/Inventory";
import DevicesIcon from "@mui/icons-material/Devices";
import AnalyticsIcon from "@mui/icons-material/Analytics";

const RADIUS = { card: 2, block: 2, pill: 2, icon: 2 };

const AssetsTrackersOverview = memo(function AssetsTrackersOverview({ assets = [], devices = [] }) {
  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, text = {}, divider, mode } = palette;
  const isDark = mode === "dark";
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const textPrimary = text?.primary || (isDark ? "#ffffff" : "#1e293b");
  const textSecondary = text?.secondary || (isDark ? "#94a3b8" : "#64748b");
  const borderColor = divider || (isDark ? "rgba(148, 163, 184, 0.18)" : "rgba(0, 40, 67, 0.08)");
  const subtleBg = isDark ? "rgba(255,255,255,0.03)" : "rgba(0, 40, 67, 0.02)";

  const { totalAssets, activeTrackers, assigned, unAssigned, trackerTotal } = useMemo(() => {
    const totalAssetsCount = assets?.length || 0;
    const deviceList = Array.isArray(devices) ? devices : [];
    const activeCount = deviceList.filter((a) => a?.device?.hasDescendents === true).length;
    let assignedCount = 0;
    let unAssignedCount = 0;
    deviceList.forEach((asset) => {
      if (asset?.device?.hasDescendents) assignedCount++;
      else unAssignedCount++;
    });
    return {
      totalAssets: totalAssetsCount,
      activeTrackers: activeCount,
      assigned: assignedCount,
      unAssigned: unAssignedCount,
      trackerTotal: assignedCount + unAssignedCount,
    };
  }, [assets, devices]);

  return (
    <Card
      component="article"
      aria-label="Assets and Trackers Overview"
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: RADIUS.card,
        border: `1px solid ${borderColor}`,
        background: isDark
          ? "linear-gradient(160deg, #042a3f 0%, #021e2d 50%, #011a26 100%)"
          : "linear-gradient(160deg, #ffffff 0%, #fafbfc 50%, #f8fafc 100%)",
        boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.2)" : "0 2px 8px rgba(0,0,0,0.06)",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Green gradient top bar */}
      <Box
        aria-hidden
        sx={{
          height: "4px",
          flexShrink: 0,
          background: isDark
            ? `linear-gradient(90deg, ${greenColor} 0%, ${navyColor} 100%)`
            : `linear-gradient(90deg, ${navyColor} 0%, ${greenColor} 100%)`,
        }}
      />
      {/* Header — premium hierarchy */}
      <Box
        component="header"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          padding: "10px 14px",
          borderBottom: `1px solid ${borderColor}`,
          flexShrink: 0,
          background: subtleBg,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: RADIUS.icon,
            background: isDark
              ? `linear-gradient(145deg, ${greenColor}22 0%, ${greenColor}08 100%)`
              : `linear-gradient(145deg, ${greenColor}18 0%, ${navyColor}06 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `1px solid ${greenColor}${isDark ? "28" : "22"}`,
          }}
        >
          <AnalyticsIcon sx={{ color: isDark ? greenColor : navyColor, fontSize: "1.125rem" }} />
        </Box>
        <Box>
          <Typography
            component="h2"
            sx={{
              fontWeight: 700,
              color: isDark ? greenColor : navyColor,
              fontSize: "1rem",
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
            }}
          >
            Assets & Trackers
          </Typography>
          <Typography sx={{ color: textSecondary, fontSize: "0.6875rem", fontWeight: 500, letterSpacing: "0.02em" }}>
            Overview
          </Typography>
        </Box>
      </Box>

      <CardContent
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          padding: "10px 12px !important",
          "&:last-of-type": { pb: 1 },
        }}
      >
        {/* Row 1: Total Assets + Active Trackers — premium metric blocks */}
        <Box sx={{ display: "flex", gap: 1, marginBottom: 1 }}>
          <Box
            role="region"
            aria-label={`Total Assets: ${totalAssets}`}
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              padding: "8px 10px",
              borderRadius: RADIUS.block,
              background: isDark
                ? "linear-gradient(135deg, rgba(0, 40, 67, 0.5) 0%, rgba(0, 40, 67, 0.2) 100%)"
                : "linear-gradient(135deg, rgba(0, 40, 67, 0.08) 0%, rgba(0, 40, 67, 0.03) 100%)",
              border: `1px solid ${navyColor}${isDark ? "35" : "18"}`,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: RADIUS.icon,
                background: `${navyColor}${isDark ? "40" : "18"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <InventoryIcon sx={{ color: navyColor, fontSize: "1.125rem" }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontVariantNumeric: "tabular-nums",
                  fontSize: "1.375rem",
                  fontWeight: 700,
                  color: textPrimary,
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                }}
              >
                {totalAssets.toLocaleString()}
              </Typography>
              <Typography sx={{ fontSize: "0.625rem", fontWeight: 600, color: textSecondary, letterSpacing: "0.03em", textTransform: "uppercase" }}>
                Total Assets
              </Typography>
            </Box>
          </Box>
          <Box
            role="region"
            aria-label={`Active Trackers: ${activeTrackers}`}
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              padding: "8px 10px",
              borderRadius: RADIUS.block,
              background: isDark
                ? "linear-gradient(135deg, rgba(160, 196, 109, 0.28) 0%, rgba(160, 196, 109, 0.1) 100%)"
                : "linear-gradient(135deg, rgba(160, 196, 109, 0.14) 0%, rgba(160, 196, 109, 0.05) 100%)",
              border: `1px solid ${greenColor}${isDark ? "38" : "28"}`,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: RADIUS.icon,
                background: `${greenColor}${isDark ? "35" : "22"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DevicesIcon sx={{ color: greenColor, fontSize: "1.125rem" }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontVariantNumeric: "tabular-nums",
                  fontSize: "1.375rem",
                  fontWeight: 700,
                  color: textPrimary,
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                }}
              >
                {activeTrackers.toLocaleString()}
              </Typography>
              <Typography sx={{ fontSize: "0.625rem", fontWeight: 600, color: textSecondary, letterSpacing: "0.03em", textTransform: "uppercase" }}>
                Active Trackers
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Row 2: Tracker assignment — grouped with clear label */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            border: `1px solid ${borderColor}`,
            borderRadius: RADIUS.block,
            padding: "8px 10px",
            background: subtleBg,
          }}
        >
          <Typography sx={{ fontSize: "0.625rem", fontWeight: 700, color: textSecondary, marginBottom: 0.5, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Tracker assignment
          </Typography>
          <Box sx={{ display: "flex", gap: 0.75, marginBottom: 0.5 }}>
            <Box
              sx={{
                flex: 1,
                padding: "5px 8px",
                borderRadius: RADIUS.pill,
                background: `${greenColor}12`,
                border: `1px solid ${greenColor}28`,
                textAlign: "center",
                transition: "background 0.2s ease",
              }}
            >
              <Typography sx={{ fontVariantNumeric: "tabular-nums", fontSize: "1.125rem", fontWeight: 700, color: greenColor }}>{assigned}</Typography>
              <Typography sx={{ fontSize: "0.5625rem", color: textSecondary, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Assigned</Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                padding: "5px 8px",
                borderRadius: RADIUS.pill,
                background: isDark ? "rgba(59, 130, 246, 0.14)" : "rgba(59, 130, 246, 0.08)",
                border: isDark ? "1px solid rgba(59, 130, 246, 0.28)" : "1px solid rgba(59, 130, 246, 0.2)",
                textAlign: "center",
                transition: "background 0.2s ease",
              }}
            >
              <Typography sx={{ fontVariantNumeric: "tabular-nums", fontSize: "1.125rem", fontWeight: 700, color: "#3b82f6" }}>{unAssigned}</Typography>
              <Typography sx={{ fontSize: "0.5625rem", color: textSecondary, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Unassigned</Typography>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
minHeight: "112px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            }}
            aria-label={`Pie chart: ${assigned} assigned, ${unAssigned} unassigned trackers`}
            role="img"
          >
            <PieChart
              height={112}
              width={180}
              margin={{ top: 2, bottom: 22, left: 4, right: 4 }}
              series={[
                {
                  data: [
                    { id: 0, label: "Assigned", value: assigned || 0, color: greenColor },
                    { id: 1, label: "Unassigned", value: unAssigned || 0, color: "#3b82f6" },
                  ],
                  innerRadius: 24,
                  outerRadius: 42,
                  paddingAngle: 2,
                  cornerRadius: 2,
                  highlightScope: { fade: "global", highlight: "item" },
                  faded: { innerRadius: 18, additionalRadius: -4, color: "gray" },
                },
              ]}
              slotProps={{
                legend: {
                  direction: "row",
                  padding: { right: 16 },
                  position: { vertical: "bottom", horizontal: "right" },
                  itemMarkWidth: 10,
                  itemMarkHeight: 10,
                  labelStyle: {
                    fontSize: 10,
                    fontWeight: 600,
                    fontFamily: '"Lato", sans-serif',
                    fill: textSecondary,
                    letterSpacing: "0.02em",
                  },
                },
              }}
            />
          </Box>
          <Box
            role="status"
            aria-label={`Total trackers: ${trackerTotal}`}
            sx={{
              padding: "5px 10px",
              borderRadius: RADIUS.pill,
              background: isDark ? "rgba(148, 163, 184, 0.08)" : "rgba(0, 40, 67, 0.04)",
              border: `1px solid ${borderColor}`,
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: "0.6875rem", color: textSecondary, fontWeight: 600 }}>
              Total trackers: <strong style={{ color: isDark ? greenColor : navyColor, fontVariantNumeric: "tabular-nums" }}>{trackerTotal}</strong>
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
});

export default AssetsTrackersOverview;
